import React, { useEffect, useState, useRef } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { CheckCircle2, XCircle } from "lucide-react";
import { api } from "../lib/api";
import { useLang } from "../contexts/LanguageContext";
import { useCart } from "../contexts/CartContext";

const CheckoutSuccess = () => {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const { t } = useLang();
  const { clear } = useCart();
  const [status, setStatus] = useState("polling");
  const [data, setData] = useState(null);
  const attempts = useRef(0);

  useEffect(() => {
    if (!sessionId) { setStatus("error"); return; }
    let cancelled = false;

    const poll = async () => {
      if (cancelled) return;
      attempts.current += 1;
      try {
        const r = await api.get(`/checkout/status/${sessionId}`);
        setData(r.data);
        if (r.data.payment_status === "paid") {
          setStatus("paid");
          clear();
          return;
        }
        if (r.data.status === "expired") { setStatus("expired"); return; }
        if (attempts.current >= 8) { setStatus("timeout"); return; }
        setTimeout(poll, 2000);
      } catch (e) {
        if (attempts.current >= 5) { setStatus("error"); return; }
        setTimeout(poll, 2000);
      }
    };

    poll();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sessionId]);

  return (
    <div data-testid="checkout-success-page" className="min-h-screen flex items-center justify-center bg-[var(--monzal-bg)]">
      <div className="max-w-md mx-auto text-center px-6">
        {status === "polling" && (
          <>
            <div className="w-12 h-12 mx-auto border-2 border-[var(--monzal-gold)] border-t-transparent rounded-full animate-spin" />
            <div className="overline mt-8">{t("checkout.checking")}</div>
          </>
        )}
        {status === "paid" && (
          <>
            <CheckCircle2 size={48} className="text-[var(--monzal-emerald)] mx-auto" />
            <h1 className="font-display font-light text-3xl text-white tracking-tight mt-6" data-testid="checkout-success-title">{t("checkout.success")}</h1>
            <p className="text-gray-400 mt-4">{t("checkout.successSub")}</p>
            {data && (
              <div className="mt-8 inline-flex gap-3">
                <span className="tag-gold">${(data.amount_total / 100).toLocaleString()} {(data.currency || "usd").toUpperCase()}</span>
                <span className="tag-tech">{data.payment_status?.toUpperCase()}</span>
              </div>
            )}
            <Link to="/" className="btn-gold mt-10 inline-flex" data-testid="checkout-back-home">
              {t("checkout.backHome")}
            </Link>
          </>
        )}
        {(status === "error" || status === "expired" || status === "timeout") && (
          <>
            <XCircle size={48} className="text-red-500 mx-auto" />
            <h1 className="font-display font-light text-3xl text-white tracking-tight mt-6">{t("checkout.failed")}</h1>
            <Link to="/cart" className="btn-ghost mt-10 inline-flex">{t("nav.cart")}</Link>
          </>
        )}
      </div>
    </div>
  );
};

export default CheckoutSuccess;
